import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthJourneyService, GrowthBusinessDiscoveryListItem } from './growth-journey.service';
import {
  DISCOVERY_STATUS_I18N_KEYS,
  discoveryStatusSeverity as resolveDiscoveryStatusSeverity,
  normalizeDiscoveryStatus
} from './growth-status.util';

@Component({
  standalone: true,
  selector: 'ain-growth-business-discovery-list',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    TagModule,
    AinDatePipe,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.journey.discovery.title')"
      [description]="t('growth.journey.discovery.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.journey.discovery.help.title'"
        [descriptionKey]="'growth.journey.discovery.help.description'"
        [bulletKeys]="['growth.journey.discovery.help.bullets.swotInput']" />

      <section class="card">
        <div class="toolbar">
          <a pButton [routerLink]="['/growth/journey/discoveries/new']" [label]="t('growth.journey.discovery.actions.new')"></a>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>

        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
        } @else if (items().length === 0) {
          <ain-state type="empty" [title]="t('growth.journey.discovery.empty')" />
        } @else {
          <p-table [value]="items()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.journey.discovery.columns.title') }}</th>
                <th>{{ t('growth.journey.discovery.columns.status') }}</th>
                <th>{{ t('growth.journey.discovery.columns.locale') }}</th>
                <th>{{ t('growth.journey.discovery.columns.createdAt') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>
                  <a [routerLink]="['/growth/journey/discoveries', row.id]" class="row-link">{{ row.title }}</a>
                </td>
                <td>
                  <p-tag [value]="discoveryStatusLabel(row.status)" [severity]="discoveryStatusSeverity(row.status)" />
                </td>
                <td>{{ row.locale }}</td>
                <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
                <td class="actions">
                  <a
                    pButton
                    size="small"
                    severity="secondary"
                    [routerLink]="['/growth/journey/discoveries', row.id]"
                    [label]="t('common.open')"></a>
                  <button
                    pButton
                    type="button"
                    size="small"
                    severity="help"
                    [label]="t('growth.journey.discovery.actions.continueSwot')"
                    [disabled]="!canContinueToSwot(row)"
                    (click)="continueToSwot(row)"></button>
                  <button
                    pButton
                    type="button"
                    size="small"
                    severity="success"
                    [label]="t('growth.journey.guided.actions.startJourney')"
                    [disabled]="!canStartGuidedJourney(row)"
                    [loading]="startingJourneyId() === row.id"
                    (click)="startGuidedJourney(row)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .row-link { color: inherit; font-weight: 600; text-decoration: none; }
    .row-link:hover { text-decoration: underline; }
  `]
})
export class GrowthBusinessDiscoveryListComponent implements OnInit {
  private readonly journey = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<GrowthBusinessDiscoveryListItem[]>([]);
  readonly startingJourneyId = signal<string | null>(null);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.journey.listDiscoveries().subscribe({
      next: items => {
        this.items.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  discoveryStatusLabel(status: unknown): string {
    const normalized = normalizeDiscoveryStatus(status);
    return this.t(DISCOVERY_STATUS_I18N_KEYS[normalized]);
  }

  discoveryStatusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveDiscoveryStatusSeverity(status);
  }

  canContinueToSwot(row: GrowthBusinessDiscoveryListItem): boolean {
    return normalizeDiscoveryStatus(row.status) === 'ReadyForSwot';
  }

  canStartGuidedJourney(row: GrowthBusinessDiscoveryListItem): boolean {
    return normalizeDiscoveryStatus(row.status) === 'ReadyForSwot';
  }

  startGuidedJourney(row: GrowthBusinessDiscoveryListItem): void {
    if (!this.canStartGuidedJourney(row)) {
      return;
    }

    this.startingJourneyId.set(row.id);
    this.journey.startJourney(row.id).subscribe({
      next: created => {
        this.startingJourneyId.set(null);
        this.router.navigate(['/growth/journey/journeys', created.id]);
      },
      error: () => {
        this.startingJourneyId.set(null);
        this.load();
      }
    });
  }

  continueToSwot(row: GrowthBusinessDiscoveryListItem): void {
    if (!this.canContinueToSwot(row)) {
      return;
    }

    this.router.navigate(['/growth/journey/discoveries', row.id], { fragment: 'swot' });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
